// app/features/industry-workflows/services_and_hooks/useIndustryWorkflowsQueryHook.ts
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useDataFetchingHook } from '@/app/shared/hooks/useDataFetchingHook';

import type { IndustryWorkflowDetail,IndustryWorkflowInput } from '../industry-workflow.interface';

export const QUERY_KEYS = {
  workflows: ['industry-workflows'] as const,
  workflowsList: (filters?: any) => ['industry-workflows', 'list', filters] as const,
  workflowDetail: (id: string) => ['industry-workflows', 'detail', id] as const,
  workflowsByIndustry: (industryId: string) => ['industry-workflows', 'by-industry', industryId] as const,
};

export function useIndustryWorkflowsQueryHook(filters?: {
  industry_id?: string;
  limit?: number;
  offset?: number;
  includeUnpublished?: boolean;
}) {
  const params = new URLSearchParams({
    limit: String(filters?.limit || 20),
    offset: String(filters?.offset || 0),
    ...(filters?.industry_id && { industry_id: filters.industry_id })
  });

  // Only filter by is_published if not including unpublished
  if (!filters?.includeUnpublished) {
    params.set('is_published', 'true');
  }

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: QUERY_KEYS.workflowsList(filters),
    queryFn: async () => {
      const response = await fetch(`/api/industry-workflows?${params}`);
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to fetch workflows');
      }
      return response.json();
    },
  });

  // Integrate global loading/error handling (Blueprint pattern)
  useDataFetchingHook({
    isLoading,
    isError,
    error: error as Error | null,
    errorMessage: 'Failed to fetch industry workflows',
  });

  return { 
    workflows: data?.workflows || [], 
    total: data?.total || 0,
    isLoading, 
    isError, 
    error,
    refetch 
  };
}

export function useIndustryWorkflowByIdQueryHook(id: string) {
  const { data, isLoading, isError, error, refetch } = useQuery<IndustryWorkflowDetail>({
    queryKey: QUERY_KEYS.workflowDetail(id),
    queryFn: async () => {
      const response = await fetch(`/api/industry-workflows/${id}`);
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to fetch workflow');
      }
      return response.json();
    },
    enabled: !!id,
    refetchInterval: (query) => {
      // Poll every 2 seconds if still generating
      const data = query?.state?.data;
      if (data && ['pending', 'context_loading', 'generating_problem_definition', 
          'generating_workflows', 'generating_content', 'assembling', 'storing'].includes(data.generation_status)) {
        return 2000;
      }
      return false;
    }
  });

  // Integrate global loading/error handling (Blueprint pattern)
  useDataFetchingHook({
    isLoading,
    isError,
    error: error as Error | null,
    errorMessage: 'Failed to fetch workflow details',
  });

  return { workflow: data, isLoading, isError, error, refetch };
}

export function useIndustryWorkflowsByIndustryQueryHook(industryId: string) {
  return useIndustryWorkflowsQueryHook({ industry_id: industryId });
}

export function useCreateIndustryWorkflowMutation() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (input: IndustryWorkflowInput) => {
      const response = await fetch('/api/industry-workflows/create', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(input)
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create workflow');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.workflows });
    },
  });
}

